// ─── Shared education system data ─────────────────────────────────────────────
// Imported by account-preferences.tsx, ProfileStep.jsx, and anywhere else that
// needs country / exam body / class level lookups.

export interface Country {
  code: string
  name: string
}

export interface ExamBody {
  code: string
  name: string
}

export const COUNTRIES: Country[] = [
  { code: 'NG', name: 'Nigeria' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'GH', name: 'Ghana' },
  { code: 'KE', name: 'Kenya' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'US', name: 'United States' },
]

export const EXAM_BODIES: Record<string, ExamBody[]> = {
  NG: [
    { code: 'WAEC',   name: 'WAEC (West African Examinations Council)' },
    { code: 'NECO',   name: 'NECO (National Examinations Council)' },
    { code: 'NABTEB', name: 'NABTEB' },
  ],
  GB: [
    { code: 'GCSE',    name: 'GCSE' },
    { code: 'A_LEVEL', name: 'A-Levels' },
    { code: 'IB',      name: 'International Baccalaureate' },
  ],
  GH: [
    { code: 'WAEC_GH', name: 'WAEC (Ghana)' },
    { code: 'BECE',    name: 'BECE' },
  ],
  KE: [
    { code: 'KCPE', name: 'KCPE' },
    { code: 'KCSE', name: 'KCSE' },
  ],
  ZA: [
    { code: 'NSC', name: 'National Senior Certificate' },
  ],
  US: [
    { code: 'SAT', name: 'SAT / AP' },
    { code: 'IB',  name: 'International Baccalaureate' },
  ],
}

export const CLASS_LEVELS: Record<string, string[]> = {
  WAEC:    ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'],
  NECO:    ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'],
  NABTEB:  ['ND1', 'ND2', 'ND3'],
  GCSE:    ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
  A_LEVEL: ['Year 12', 'Year 13'],
  IB:      ['MYP 1', 'MYP 2', 'MYP 3', 'MYP 4', 'MYP 5', 'DP1', 'DP2'],
  WAEC_GH: ['JHS1', 'JHS2', 'JHS3', 'SHS1', 'SHS2', 'SHS3'],
  BECE:    ['JHS1', 'JHS2', 'JHS3'],
  KCPE:    ['Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8'],
  KCSE:    ['Form 1', 'Form 2', 'Form 3', 'Form 4'],
  NSC:     ['Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'],
  SAT:     ['Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'],
}

/**
 * Returns the eligible classes for a student at `currentClass` under `examBody`.
 * Always includes the current class (pre-selected, can't deselect) plus up to 2
 * classes below it in the same tier — so a maximum of 3 total.
 *
 * Example (WAEC, SS3): ['SS3', 'SS2', 'SS1']
 * Example (WAEC, SS1): ['SS1']  — no classes below it in SS tier
 * Example (WAEC, JSS2): ['JSS2', 'JSS1']
 */
export function getEligibleClasses(examBody: string, currentClass: string): string[] {
  const levels = CLASS_LEVELS[examBody] ?? []
  const idx = levels.indexOf(currentClass)
  if (idx === -1) return currentClass ? [currentClass] : []
  const start = Math.max(0, idx - 2)
  // Return slice from start to idx (inclusive), reversed so current class is first
  return levels.slice(start, idx + 1).reverse()
}
